import React, { useState, useEffect, useMemo, useRef } from "react";
import Menubar from "../Menubar/Menubar";
import { Outlet } from "react-router-dom";
import "../Dashboard/Dashboard.css";
import axios from "axios";
import { FaEye } from "react-icons/fa";
import { MdCancel } from "react-icons/md";
import {
  Share2,
  MessageCircle,
  LayoutDashboard,
  Globe,
  Link as LinkIcon,
} from "lucide-react";
import { API_URL } from "../../Config";
import { Search } from "lucide-react";
import SearchProperty from "./SearchProperty";
import { createPortal } from "react-dom";

// const buildWebsiteUrl = (property) => {
//   const isHotel = property.propertytype === "Hotel";
//   return isHotel
//     ? `https://zorbastays.com/Individualhotel/${property.id}/Hotel/${encodeURIComponent(property.propertyname)}/${encodeURIComponent(property.destination)}/${encodeURIComponent(property.location)}`
//     : `https://zorbastays.com/IndividualProperty/${property.id}/${encodeURIComponent(property.propertytype)}/${encodeURIComponent(property.propertyname)}/${encodeURIComponent(property.destination)}/${encodeURIComponent(property.location)}/${property.maxadult}/${property.maxbed}/${property.maxbath || 0}`;
// };
const buildWebsiteUrl = (property) => {
  const isHotel = property.propertytype === "Hotel";
  const type = isHotel ? "hotel" : "villa";
  const guest = isHotel ? 0 : property.maxadult;
  const bedroom = isHotel ? 0 : property.maxbed;
  const bathroom = isHotel ? 0 : property.maxbath || 0;

  return `https://zorbastays.com/property-details/${property.id}/${type}/${encodeURIComponent(property.propertyname)}/${encodeURIComponent(property.destination)}/${encodeURIComponent(property.location)}/guest=${guest}/bedroom=${bedroom}/bathroom=${bathroom}`;
};
// NOTE: confirm the exact query param your dashboard edit page expects —
// this assumes ?id=<propertyId> based on EditPropertyDetailElementor.
const buildDashboardUrl = (property) =>
  `/EditPropertyDetailElementor?id=${property.id}&mode=view`;
const buildWhatsAppShareUrl = (property) => {
  const websiteUrl = buildWebsiteUrl(property);
  const message = `Check out ${property.propertyname} in ${property.location}, ${property.destination}: ${websiteUrl}`;
  return `https://wa.me/?text=${encodeURIComponent(message)}`;
};
const ShareMenu = ({ property, align = "right", buttonClassName = "" }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  if (!property) return null;

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right + window.scrollX - 224, // 224px = w-56 menu width, right-aligned
      });
    }
    setOpen((prev) => !prev);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(buildWebsiteUrl(property));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  return (
    <div className="inline-block">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="text-gray-400 hover:text-[#1B2826] transition-colors ${buttonClassName}"
        title="Share"
      >
        <Share2 size={16} />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: "absolute",
              top: menuPos.top,
              left: menuPos.left,
            }}
            className="w-52 ml-52 bg-white border rounded-lg shadow-lg z-[9999] py-1 text-sm"
          >
            <a
              href={buildWhatsAppShareUrl(property)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-4 py-2 hover:bg-green-50 text-gray-700"
            >
              <MessageCircle size={15} />
              Share on WhatsApp
            </a>

            <a
              href={buildDashboardUrl(property)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-4 py-2 hover:bg-green-50 text-gray-700"
            >
              <LayoutDashboard size={15} />
              Open in dashboard
            </a>

            <a
              href={buildWebsiteUrl(property)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-4 py-2 hover:bg-green-50 text-gray-700"
            >
              <Globe size={15} />
              Open on website
            </a>

            <button
              type="button"
              onClick={() => {
                handleCopyLink();
                setOpen(false);
              }}
              className="w-full flex items-center gap-2 px-4 py-2 hover:bg-green-50 text-gray-700 text-left"
            >
              <LinkIcon size={15} />
              {copied ? "Copied!" : "Copy link"}
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
};
const SmartScheduler = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [viewProperty, setViewProperty] = useState(null);
  const [loadingViewProperty, setLoadingViewProperty] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [amenities, setAmenities] = useState([]);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [bedFilter, setBedFilter] = useState("");
  const [amenitiesOpen, setAmenitiesOpen] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const amenitiesRef = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const savedState = localStorage.getItem("menubarOpen");
    if (savedState !== null) return JSON.parse(savedState);
    return window.innerWidth >= 1024;
  });
  const [ownerDropdownOpen, setOwnerDropdownOpen] = useState(false);
  const ownerRef = useRef(null);
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (mobile) setIsSidebarOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  useEffect(() => {
    const fetchAmenities = async () => {
      try {
        const res = await axios.get(`${API_URL}/getAmenities`);
        if (res.data.success) setAmenities(res.data.data);
      } catch (err) {
        console.error("Failed to fetch amenities:", err);
      }
    };
    fetchAmenities();
  }, []);
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (amenitiesRef.current && !amenitiesRef.current.contains(e.target)) {
        setAmenitiesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ownerRef.current && !ownerRef.current.contains(e.target)) {
        setOwnerDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const [searchProperty, setSearchProperty] = useState("");
  const [propertytype, setpropertytype] = useState("");
  const [guests, setGuests] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [visibleCount, setVisibleCount] = useState(20);
  const [destinationFilter, setdestinationFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [viewPropertyData, setViewPropertyData] = useState(null);
  // ── Real data state ──
  const [properties, setProperties] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState("");
  const owners = [
    ...new Set(properties.map((p) => p.propertyowner).filter(Boolean)),
  ].sort();

  const [blockedMap, setBlockedMap] = useState({}); // { propertyname: Set of "YYYY-MM-DD" }
  const [loadingProperties, setLoadingProperties] = useState(true);
  const [loadingBlocked, setLoadingBlocked] = useState(false);
  // near other state
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setSearchProperty(searchInput), 250);
    return () => clearTimeout(t);
  }, [searchInput]);
  // ── Fetch all properties ──
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoadingProperties(true);
        const res = await axios.get(`${API_URL}/properties`);
        setProperties(res.data);
      } catch (err) {
        console.error("Failed to fetch properties:", err);
      } finally {
        setLoadingProperties(false);
      }
    };
    fetchProperties();
  }, []);

  // ── Bulk fetch blocked dates (once on mount) ──
  useEffect(() => {
    let cancelled = false;

    const fetchAllBlocked = async () => {
      setLoadingBlocked(true);
      try {
        const res = await axios.get(`${API_URL}/blocked-dates-all`);
        if (cancelled) return;

        if (res.data.success) {
          const map = {};
          for (const [name, { airbnb, db }] of Object.entries(
            res.data.blocked,
          )) {
            map[name] = {
              airbnb: new Set(airbnb),
              db: new Set(db),
            };
          }
          setBlockedMap(map);
        }
      } catch (err) {
        console.error("Failed to fetch blocked dates:", err);
      } finally {
        if (!cancelled) setLoadingBlocked(false);
      }
    };

    fetchAllBlocked();
    return () => {
      cancelled = true;
    };
  }, []);
  // { [propertyId]: { "YYYY-MM-DD": price } }  — comes ONLY from property_price_global
  const [priceMap, setPriceMap] = useState({});
  const [loadingPrices, setLoadingPrices] = useState(false);

  useEffect(() => {
    const fetchAllPrices = async () => {
      setLoadingPrices(true);
      try {
        const res = await axios.get(
          `${API_URL}/property-prices-by-month?year=${selectedYear}&month=${selectedMonth + 1}`,
        );
        setPriceMap(res.data.success ? res.data.prices || {} : {});
      } catch (err) {
        console.error("Failed to fetch property prices:", err);
        setPriceMap({});
      } finally {
        setLoadingPrices(false);
      }
    };
    fetchAllPrices();
  }, [selectedMonth, selectedYear]);
  // ── Helpers ──
  const years = Array.from(
    { length: 11 },
    (_, i) => new Date().getFullYear() - 5 + i,
  );

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
  const today = new Date();
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();
  const todayDate = today.getDate();

  // Only show days from today onwards; hide all days for past months
  const visibleDays = Array.from(
    { length: daysInMonth },
    (_, i) => i + 1,
  ).filter((day) => {
    if (selectedYear > todayYear) return true;
    if (selectedYear === todayYear && selectedMonth > todayMonth) return true;
    if (selectedYear === todayYear && selectedMonth === todayMonth)
      return day >= todayDate;
    return false; // past month — hide all days
  });
  const isBlocked = (propertyname, day) => {
    const dateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return blockedMap[propertyname]?.has(dateStr) ?? false;
  };

  // ── Unique destinations and locations for filters ──
  const destinations = [
    ...new Set(properties.map((p) => p.destination).filter(Boolean)),
  ].sort();
  const locations = [
    ...new Set(
      properties
        .filter(
          (p) => !destinationFilter || p.destination === destinationFilter,
        )
        .map((p) => p.location)
        .filter(Boolean),
    ),
  ].sort();
  useEffect(() => {
    if (selectedYear === todayYear && selectedMonth < todayMonth) {
      setSelectedMonth(todayMonth);
    }
  }, [selectedYear]);
  useEffect(() => {
    if (properties.length) {
      // console.log("Sample property:", properties[0]);
      // console.log(
      //   "Sample addons:",
      //   properties[0].addons,
      //   typeof properties[0].addons,
      // );
    }
  }, [properties]);
  const getPropertyAmenityIds = (property) => {
    const raw = property.addons ?? property.addons ?? property.amenities;
    if (!raw) return [];

    // Already an array
    if (Array.isArray(raw)) {
      return raw
        .map((v) => Number(typeof v === "object" ? v.id : v))
        .filter((n) => !isNaN(n));
    }

    // Comma-separated string
    return String(raw)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map(Number)
      .filter((n) => !isNaN(n));
  };
  const toggleAmenity = (id) => {
    setSelectedAmenities((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id],
    );
  };
  // ── Filter properties ──
  const filteredProperties = useMemo(() => {
    return properties.filter((p) => {
      const matchesSearch = p.propertyname
        .toLowerCase()
        .includes(searchProperty.toLowerCase());
      const matchesType = !propertytype || p.propertytype === propertytype;
      const matchesGuests =
        !guests || p.maxadult + (p.maxchild || 0) >= Number(guests);
      const matchesDest =
        !destinationFilter || p.destination === destinationFilter;
      const matchesLoc = !locationFilter || p.location === locationFilter;
      const matchesBed = !bedFilter || Number(p.maxbed) === Number(bedFilter);
      const matchesAmenities =
        selectedAmenities.length === 0 ||
        selectedAmenities.every((id) => getPropertyAmenityIds(p).includes(id));
      const matchesOwner =
        !ownerFilter ||
        p.propertyowner?.toLowerCase().includes(ownerFilter.toLowerCase());
      return (
        matchesSearch &&
        matchesType &&
        matchesGuests &&
        matchesDest &&
        matchesLoc &&
        matchesBed &&
        matchesAmenities &&
        matchesOwner
      );
    });
  }, [
    properties,
    searchProperty,
    propertytype,
    guests,
    destinationFilter,
    locationFilter,
    bedFilter,
    selectedAmenities,
    ownerFilter,
  ]);

  const visibleProperties = filteredProperties.slice(0, visibleCount);

  useEffect(() => {
    setVisibleCount(20);
  }, [
    searchProperty,
    propertytype,
    guests,
    destinationFilter,
    locationFilter,
    bedFilter,
    selectedAmenities,
    ownerFilter,
  ]);
  const filteredOwners = owners.filter((owner) =>
    owner.toLowerCase().includes(ownerFilter.toLowerCase()),
  );

  const [active, setActive] = useState("Calendar");

  const options = ["Calendar", "Search Availability"];

  const handleTabChange = (tab) => {
    setActive(tab);
  };

  return (
    <div className="flex bg-[#F8F9FA]">
      <Menubar
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
        isMobile={isMobile}
      />

      <main
        className={`flex-1 min-w-0 p-5 lg:p-6 transition-all duration-500 w-full ${
          isMobile ? "ml-0" : isSidebarOpen ? "ml-64" : "ml-16"
        }`}
      >
        <Outlet />

        <p className="font-semibold text-xl text-gray-700 ml-10 lg:ml-0">
          Smart Scheduler
        </p>

        {/* Segmented button */}
        <div className="mt-5">
          <div className="inline-flex border border-gray-300 rounded-lg overflow-hidden text-sm font-medium shadow-sm">
            {options.map((option) => (
              <button
                key={option}
                onClick={() => handleTabChange(option)}
                className={`px-4 py-2 transition-colors duration-200 focus:outline-none ${
                  active === option
                    ? "bg-[#1D2826] text-white"
                    : "bg-white text-gray-500 hover:bg-green-50"
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>

        {active === "Calendar" && (
          <div className="mt-8">
            {/* Filters */}
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-3">
                {/* Property Type */}
                <select
                  value={propertytype}
                  onChange={(e) => setpropertytype(e.target.value)}
                  className="border border-gray-300 rounded-lg px-4 py-3 text-sm w-48 focus-within:border-black focus-within:ring-1 focus-within:ring-black"
                >
                  <option value="">All Property Types</option>
                  <option value="Villa">Villa</option>
                  <option value="Apartment">Apartment</option>
                  <option value="Hotel">Hotel</option>
                </select>

                {/* Month */}
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="border border-gray-300 rounded-lg px-4 py-3 text-sm w-40 focus-within:border-black focus-within:ring-1 focus-within:ring-black"
                >
                  {monthNames.map((month, index) => {
                    const isPast =
                      selectedYear === todayYear && index < todayMonth;

                    return (
                      <option key={month} value={index} disabled={isPast}>
                        {month}
                      </option>
                    );
                  })}
                </select>

                {/* Year */}
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="border border-gray-300 rounded-lg px-4 py-3 text-sm w-32 focus-within:border-black focus-within:ring-1 focus-within:ring-black"
                >
                  {years.map((year) => (
                    <option key={year} value={year} disabled={year < todayYear}>
                      {year}
                    </option>
                  ))}
                </select>

                {/* destination */}
                <select
                  value={destinationFilter}
                  onChange={(e) => {
                    setdestinationFilter(e.target.value);
                    setLocationFilter("");
                  }}
                  className="border border-gray-300 rounded-lg px-4 py-3 text-sm w-48 focus-within:border-black focus-within:ring-1 focus-within:ring-black"
                >
                  <option value="">All destinations</option>

                  {destinations.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>

                {/* Location */}
                <select
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-4 py-3 text-sm w-48 focus-within:border-black focus-within:ring-1 focus-within:ring-black"
                >
                  <option value="">All Locations</option>

                  {locations.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </select>

                {/* More Filters */}
                <div className="relative">
                  <button
                    onClick={() => setShowMoreFilters((prev) => !prev)}
                    className="border border-gray-300 rounded-lg px-4 py-3 text-sm bg-white focus-within:border-black focus-within:ring-1 focus-within:ring-black"
                  >
                    More Filters ▾
                  </button>

                  {showMoreFilters && (
                    <div className="absolute right-0 mt-2 w-96 bg-white border rounded-xl shadow-xl p-5 z-50">
                      <h3 className="font-semibold text-gray-700 mb-4">
                        Advanced Filters
                      </h3>

                      <div className="grid grid-cols-2 gap-4">
                        {/* Guests */}
                        <input
                          type="text"
                          placeholder="Guests"
                          value={guests}
                          onChange={(e) =>
                            setGuests(e.target.value.replace(/\D/g, ""))
                          }
                          className="border rounded-lg px-3 py-2 text-sm focus-within:border-black focus-within:ring-1 focus-within:ring-black"
                        />

                        {/* BHK */}
                        <input
                          type="text"
                          placeholder="BHK"
                          value={bedFilter}
                          onChange={(e) =>
                            setBedFilter(e.target.value.replace(/\D/g, ""))
                          }
                          className="border rounded-lg px-3 py-2 text-sm focus-within:border-black focus-within:ring-1 focus-within:ring-black"
                        />

                        {/* Owner Search */}
                        <div
                          className="relative flex-1 min-w-[250px]"
                          ref={ownerRef}
                        >
                          <input
                            type="text"
                            placeholder="Managed By..."
                            value={ownerFilter}
                            onFocus={() => setOwnerDropdownOpen(true)}
                            onChange={(e) => {
                              setOwnerFilter(e.target.value);
                              setOwnerDropdownOpen(true);
                            }}
                            className="w-full border rounded-lg px-3 py-2 text-sm col-span-2 focus-within:border-black focus-within:ring-1 focus-within:ring-black"
                          />

                          {ownerDropdownOpen && (
                            <div className="absolute left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
                              {filteredOwners.length === 0 ? (
                                <div className="px-4 py-3 text-sm text-gray-400">
                                  No owners found
                                </div>
                              ) : (
                                filteredOwners.map((owner) => (
                                  <button
                                    key={owner}
                                    type="button"
                                    onClick={() => {
                                      setOwnerFilter(owner);
                                      setOwnerDropdownOpen(false);
                                    }}
                                    className="w-full text-left px-4 py-3 hover:bg-green-50 transition"
                                  >
                                    <>
                                      <span className="font-semibold">
                                        {owner.substring(0, ownerFilter.length)}
                                      </span>
                                      {owner.substring(ownerFilter.length)}
                                    </>
                                  </button>
                                ))
                              )}
                            </div>
                          )}
                        </div>

                        {/* Amenities */}
                        <div className="relative col-span-2" ref={amenitiesRef}>
                          <button
                            type="button"
                            onClick={() => setAmenitiesOpen((prev) => !prev)}
                            className="w-full border rounded-lg px-3 py-2 text-sm flex justify-between items-center focus-within:border-black focus-within:ring-1 focus-within:ring-black"
                          >
                            <span>
                              {selectedAmenities.length === 0
                                ? "All Amenities"
                                : `${selectedAmenities.length} Selected`}
                            </span>

                            <span>▾</span>
                          </button>

                          {amenitiesOpen && (
                            <div className="absolute left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-white border rounded-lg shadow-lg p-2">
                              {selectedAmenities.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setSelectedAmenities([])}
                                  className="text-xs text-red-500 underline mb-2"
                                >
                                  Clear all
                                </button>
                              )}

                              {amenities
                                .filter(
                                  (a) =>
                                    !["kitchen", "bedroom"].includes(
                                      a.name.toLowerCase(),
                                    ),
                                )
                                .map((a) => (
                                  <label
                                    key={a.id}
                                    className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 rounded cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedAmenities.includes(a.id)}
                                      onChange={() => toggleAmenity(a.id)}
                                    />

                                    <img
                                      src={a.url}
                                      alt={a.name}
                                      className="w-8 h-8 object-contain"
                                    />

                                    <span className="text-sm">{a.name}</span>
                                  </label>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Clear */}
                <button
                  onClick={() => {
                    setpropertytype("");
                    setGuests("");
                    setBedFilter("");
                    setdestinationFilter("");
                    setLocationFilter("");
                    setSelectedAmenities([]);
                    setOwnerFilter("");
                  }}
                  className="border border-red-300 text-red-600 rounded-lg px-4 py-3 text-sm hover:bg-red-50"
                >
                  Clear
                </button>
              </div>
            </div>
            {/* Legend */}
            <div className="flex gap-4 mb-4 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-red-100 inline-block"></span>{" "}
                Airbnb Blocked
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-green-100 inline-block"></span>{" "}
                Zorba Booked
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-white border inline-block"></span>{" "}
                Available
              </span>
            </div>

            {loadingProperties ? (
              <div className="text-center py-10 text-gray-400">
                Loading Dates...
              </div>
            ) : (
              <>
                <div className="overflow-auto border rounded-xl">
                  <table className="min-w-max w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-gray-100 border-b text-gray-500">
                        <th className="sticky left-0 z-20 min-w-[200px] p-3 bg-gray-100">
                          <input
                            type="text"
                            placeholder="Search Property..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            className="w-full text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-0 focus:border-black px-3 py-2"
                          />
                        </th>
                        {visibleDays.map((day) => {
                          // const today = new Date();
                          const isToday =
                            day === today.getDate() &&
                            selectedMonth === today.getMonth() &&
                            selectedYear === today.getFullYear();
                          return (
                            <th
                              key={day}
                              className={`min-w-[110px] p-3 text-center whitespace-nowrap ${
                                isToday
                                  ? "bg-green-100 text-green-700 font-bold"
                                  : ""
                              }`}
                            >
                              {day} {monthNames[selectedMonth].slice(0, 3)}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>

                    <tbody>
                      {visibleProperties.map((property) => (
                        <tr key={property.id} className="border-b">
                          <td className="sticky left-0 bg-white z-10 p-3 font-medium border-r">
                            <div>
                              <img
                                src={
                                  property.imagenew
                                    ? property.imagenew
                                    : property.hotelbanner
                                }
                                alt={property.propertyname}
                                className="mx-auto w-auto h-28 aspect-[5/3] object-cover rounded-md bg-gray-100"
                              />
                            </div>

                            <div className="flex justify-between items-start gap-3 mt-3">
                              <div>
                                <div className="font-semibold">
                                  {property.propertyname}
                                </div>

                                <div className="text-xs text-gray-500 mt-1">
                                  {property.propertytype} • {property.maxadult}{" "}
                                  Guests • {property.maxbed} Beds
                                </div>

                                <div className="text-xs text-gray-400">
                                  {property.location}, {property.destination}
                                </div>

                                {/* {property.currentprice &&
                                Number(property.currentprice) !== 0 ? (
                                  <div className="text-xs text-green-700 font-semibold mt-1">
                                    ₹
                                    {Number(
                                      property.currentprice,
                                    ).toLocaleString()}
                                    /night
                                  </div>
                                ) : (
                                  <div className="text-xs text-gray-400 font-semibold mt-1">
                                    Price N/A
                                  </div>
                                )} */}
                                {(() => {
                                  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
                                  const price =
                                    priceMap[property.id]?.[todayStr];

                                  return price ? (
                                    <div className="text-xs text-green-700 font-semibold mt-1 flex items-center gap-1">
                                      <Globe
                                        size={11}
                                        className="text-blue-500"
                                        title="Price from property_price_global"
                                      />
                                      ₹{price.toLocaleString()}/night
                                    </div>
                                  ) : (
                                    <div className="text-xs text-gray-400 font-semibold mt-1">
                                      Price N/A
                                    </div>
                                  );
                                })()}
                                {/* <button
                                  type="button"
                                  onClick={() => {
                                    const isHotel =
                                      property.propertytype === "Hotel";
                                    const url = isHotel
                                      ? `https://zorbastays.com/Individualhotel/${property.id}/Hotel/${encodeURIComponent(property.propertyname)}/${encodeURIComponent(property.destination)}/${encodeURIComponent(property.location)}`
                                      : `https://zorbastays.com/IndividualProperty/${property.id}/${encodeURIComponent(property.propertytype)}/${encodeURIComponent(property.propertyname)}/${encodeURIComponent(property.destination)}/${encodeURIComponent(property.location)}/${property.maxadult}/${property.maxbed}/${property.maxbath || 0}`;

                                    window.open(url, "_blank");
                                  }}
                                  className="absolute bottom-2 mr-2 right-0 text-xs text-[#4c6863] underline hover:text-[#1B2826]"
                                >
                                  Open Property
                                </button> */}
                                <div className="absolute bottom-2 right-2 flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      window.open(
                                        buildWebsiteUrl(property),
                                        "_blank",
                                      )
                                    }
                                    className="text-xs text-[#4c6863] underline hover:text-[#1B2826]"
                                  >
                                    Open Property
                                  </button>
                                </div>
                              </div>

                              <div className="space-y-3">
                                <FaEye
                                  size={22}
                                  onClick={async () => {
                                    setViewProperty(property);

                                    setTimeout(() => {
                                      setIsDrawerOpen(true);
                                    }, 10);
                                    setViewPropertyData(null);
                                    setLoadingViewProperty(true);
                                    try {
                                      const isHotel =
                                        property.propertytype === "Hotel";

                                      const res = await axios.get(
                                        isHotel
                                          ? `${API_URL}/hotel/${property.id}`
                                          : `${API_URL}/properties/${property.id}`,
                                      );
                                      // Normalize so the rest of the drawer code works the same
                                      const data = isHotel
                                        ? {
                                            ...property,
                                            ...res.data.property,
                                            ...res.data.hotelInfo,
                                            imagenew: property.imagenew,
                                            hotelbanner: property.hotelbanner,
                                          }
                                        : {
                                            ...property,
                                            ...res.data,
                                            imagenew: property.imagenew,
                                            hotelbanner: property.hotelbanner,
                                          };

                                      setViewPropertyData(data);
                                    } catch (err) {
                                      console.error(
                                        "Failed to fetch property details:",
                                        err,
                                      );
                                    } finally {
                                      setLoadingViewProperty(false);
                                    }
                                  }}
                                  className="border rounded-full p-0.5 flex-shrink-0 cursor-pointer hover:border-green-700 hover:scale-110 transition-all duration-300"
                                />

                                <ShareMenu property={property} />
                              </div>
                            </div>
                          </td>

                          {visibleDays.map((day) => {
                            const dateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

                            const prevDayDate = new Date(
                              selectedYear,
                              selectedMonth,
                              day - 1,
                            );
                            const prevDateStr = `${prevDayDate.getFullYear()}-${String(prevDayDate.getMonth() + 1).padStart(2, "0")}-${String(prevDayDate.getDate()).padStart(2, "0")}`;

                            const blocked = blockedMap[property.propertyname];
                            const isAirbnbBlocked =
                              blocked?.airbnb?.has(dateStr) ?? false;
                            const isDbBlocked =
                              blocked?.db?.has(dateStr) ?? false;
                            const isAnyBlocked = isAirbnbBlocked || isDbBlocked;

                            const isPrevDayAvailable = !(
                              blocked?.airbnb?.has(prevDateStr) ||
                              blocked?.db?.has(prevDateStr)
                            );
                            const isHalfCheckout =
                              isAirbnbBlocked && isPrevDayAvailable;

                            const today = new Date();
                            const isToday =
                              day === today.getDate() &&
                              selectedMonth === today.getMonth() &&
                              selectedYear === today.getFullYear();

                            const price = priceMap[property.id]?.[dateStr];

                            return (
                              <td
                                key={day}
                                // Added overflow-hidden here to trap everything inside the cell
                                className={`border text-center p-2 relative overflow-hidden isolate ${isToday ? "bg-green-50" : ""}`}
                              >
                                {loadingBlocked ? (
                                  <div className="rounded-lg min-h-[72px] bg-gray-100 animate-pulse" />
                                ) : (
                                  <div
                                    // Removed inline background style. Using exact background classes now.
                                    className={`rounded-lg p-2 min-h-[72px] flex flex-col items-center justify-center relative overflow-hidden ${
                                      isAirbnbBlocked && !isHalfCheckout
                                        ? "bg-red-50 text-red-400"
                                        : isDbBlocked
                                          ? "bg-green-50 text-green-600"
                                          : isHalfCheckout
                                            ? "bg-red-50 text-red-400" // Base background for half checkout
                                            : "bg-white text-gray-800"
                                    }`}
                                  >
                                    {isHalfCheckout && (
                                      <div
                                        className="absolute inset-0 z-0 rounded-lg overflow-hidden pointer-events-none"
                                        style={{
                                          background:
                                            "linear-gradient(to bottom right, #ffffff 49.5%, #e5e7eb 49.5%, #e5e7eb 50.5%, transparent 50.5%)",
                                        }}
                                      />
                                    )}

                                    {/* Content wrapper (z-10 keeps text above the backgrounds) */}
                                    <div className="relative z-10 flex flex-col items-center justify-center w-full">
                                      {!isAnyBlocked && price && (
                                        <Globe
                                          size={12}
                                          className="text-blue-500 mb-0.5"
                                          title="Price from property_price_global"
                                        />
                                      )}

                                      <span className="text-sm font-medium">
                                        {day}
                                      </span>

                                      {isAnyBlocked ? (
                                        <span className="text-xs mt-1">
                                          {isAirbnbBlocked
                                            ? "Airbnb"
                                            : "Booked"}
                                        </span>
                                      ) : price ? (
                                        <span className="text-xs text-green-700 font-semibold mt-1">
                                          ₹{price.toLocaleString()}
                                        </span>
                                      ) : (
                                        <span className="text-xs text-gray-400 font-semibold mt-1">
                                          N/A
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/*
              {loadingBlocked && (
                <p className="text-xs text-gray-400 mt-2 text-center">
                  Loading availability data...
                </p>
              )} */}

                {visibleCount < filteredProperties.length && (
                  <div className="flex justify-center mt-5">
                    <button
                      onClick={() => setVisibleCount((prev) => prev + 10)}
                      className="px-5 py-2 rounded-lg bg-green-900 text-white text-sm font-medium hover:scale-95 hover:opacity-90 transition"
                    >
                      Load More
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {active === "Search Availability" && (
          <div>
            <SearchProperty />
          </div>
        )}
      </main>

      <div className="relative z-50">
        {viewProperty && (
          <div
            onClick={() => {
              setIsDrawerOpen(false);

              setTimeout(() => {
                setViewProperty(null);
                setViewPropertyData(null);
              }, 500);
            }}
            className={`fixed inset-0 bg-black/30 z-40 transition-opacity duration-500 ${
              isDrawerOpen ? "opacity-100" : "opacity-0"
            }`}
          />
        )}

        <div
          className={`fixed top-0 right-0 h-full w-[85%] md:w-[640px] bg-white z-50 shadow-lg transform transition-transform duration-500 ease-in-out ${
            isDrawerOpen ? "translate-x-0" : "translate-x-full"
          } panel_popupForm`}
        >
          {/* // <div className="fixed top-0 right-0 h-full w-[85%] md:w-[640px] bg-white z-50 shadow-lg"> */}
          <div className="p-4 flex justify-between items-center border-b header_popupForm">
            <h2 className="text-[#1D2826] text-lg font-semibold flex items-center">
              <span>Property Details</span>

              {viewProperty && (
                <span className="ml-4">
                  <ShareMenu property={viewProperty} />
                </span>
              )}
            </h2>

            <div className="flex gap-3 items-center">
              <button
                type="button"
                onClick={() =>
                  window.open(buildWebsiteUrl(viewProperty), "_blank")
                }
                className="zorba_btn text-sm uppercase flex items-center justify-center gap-2"
                style={{ "--clr": "#2c3d3a" }}
              >
                Open Url
                <span className="zorba_btn__icon-wrapper">
                  <svg
                    viewBox="0 0 14 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="zorba_btn__icon-svg"
                    width="10"
                  >
                    <path
                      d="M13.376 11.552l-.264-10.44-10.44-.24.024 2.28 6.96-.048L.2 12.56l1.488 1.488 9.432-9.432-.048 6.912 2.304.024z"
                      fill="currentColor"
                    ></path>
                  </svg>

                  <svg
                    viewBox="0 0 14 15"
                    fill="none"
                    width="10"
                    xmlns="http://www.w3.org/2000/svg"
                    className="zorba_btn__icon-svg zorba_btn__icon-svg--copy"
                  >
                    <path
                      d="M13.376 11.552l-.264-10.44-10.44-.24.024 2.28 6.96-.048L.2 12.56l1.488 1.488 9.432-9.432-.048 6.912 2.304.024z"
                      fill="currentColor"
                    ></path>
                  </svg>
                </span>
              </button>

              <button
                onClick={() => {
                  setIsDrawerOpen(false);
                  setTimeout(() => {
                    setViewProperty(null);
                    setViewPropertyData(null);
                  }, 500);
                }}
                className="text-gray-500 hover:text-black text-xl"
              >
                <MdCancel />
              </button>
            </div>
          </div>
          {viewProperty && (
            <div className="p-6 max-h-[90vh] overflow-y-auto">
              {/* Banner Image */}
              {loadingViewProperty ? (
                <div className="w-full h-48 rounded-xl bg-gray-100 animate-pulse mb-6" />
              ) : viewPropertyData?.imagenew ||
                viewPropertyData?.hotelbanner ? (
                <img
                  src={
                    viewPropertyData.imagenew || viewPropertyData.hotelbanner
                  }
                  alt={viewProperty.propertyname}
                  className="w-full h-auto min-h-80 object-cover rounded-xl mb-6 bg-gray-100"
                />
              ) : (
                <div className="w-full h-52 rounded-xl bg-gray-100 flex items-center justify-center text-gray-400 text-sm mb-6">
                  No image available
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                {/* Villa Name */}
                <div className="col-span-2 border border-gray-300 rounded-lg px-4 py-3 relative">
                  <span className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500">
                    Villa Name
                  </span>
                  <p className="text-sm font-semibold text-gray-800">
                    {viewProperty.propertyname}
                  </p>
                </div>

                {/* Property Type */}
                <div className="border border-gray-300 rounded-lg px-4 py-3 relative">
                  <span className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500">
                    Property Type
                  </span>
                  <p className="text-sm text-gray-800">
                    {viewProperty.propertytype}
                  </p>
                </div>

                {/* Guests */}
                <div className="border border-gray-300 rounded-lg px-4 py-3 relative">
                  <span className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500">
                    Guests
                  </span>
                  <p className="text-sm text-gray-800">
                    {viewProperty.maxadult} Adults
                  </p>
                </div>

                {/* Beds */}
                <div className="border border-gray-300 rounded-lg px-4 py-3 relative">
                  <span className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500">
                    Beds
                  </span>
                  <p className="text-sm text-gray-800">
                    {viewProperty.maxbed} Beds
                  </p>
                </div>

                {/* Baths */}
                <div className="border border-gray-300 rounded-lg px-4 py-3 relative">
                  <span className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500">
                    Bath
                  </span>
                  <p className="text-sm text-gray-800">
                    {viewProperty.maxbath ?? "-"} Baths
                  </p>
                </div>

                {/* destination */}
                <div className="border border-gray-300 rounded-lg px-4 py-3 relative">
                  <span className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500">
                    destination
                  </span>
                  <p className="text-sm text-gray-800">
                    {viewProperty.destination}
                  </p>
                </div>

                {/* Location */}
                <div className="border border-gray-300 rounded-lg px-4 py-3 relative">
                  <span className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500">
                    Location
                  </span>
                  <p className="text-sm text-gray-800">
                    {viewProperty.location}
                  </p>
                </div>

                {/* Price */}
                <div className="border border-gray-300 rounded-lg px-4 py-3 relative">
                  <span className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500">
                    Price Per Night
                  </span>
                  <p className="text-sm text-gray-800">
                    ₹{Number(viewProperty.currentprice).toLocaleString()}
                  </p>
                </div>

                {/* Map Link */}

                <div className="border border-gray-300 rounded-lg px-4 py-3 relative">
                  <span className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500">
                    Google Map
                  </span>
                  {loadingViewProperty ? (
                    <div className="h-4 w-24 bg-gray-100 animate-pulse rounded" />
                  ) : viewPropertyData?.maplink ? (
                    <a
                      href={viewPropertyData.maplink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 underline truncate block"
                    >
                      View on Map
                    </a>
                  ) : (
                    <p className="text-sm text-gray-400">Not available</p>
                  )}
                </div>
                {/* Brochure */}
                {/* <div className="border border-gray-300 rounded-lg px-4 py-3 relative">
                  <span className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500">
                    Quick Brochure
                  </span>

                  <button
                    type="button"
                    onClick={() =>
                      window.open(`/QuickBrochure/${viewProperty.id}`, "_blank")
                    }
                    className="text-sm text-blue-600 underline truncate block"
                  >
                    View Brochure
                  </button>
                </div> */}
                {viewProperty.propertytype !== "Hotel" && (
                  <div className="border border-gray-300 rounded-lg px-4 py-3 relative">
                    <span className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500">
                      Quick Brochure
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        window.open(
                          `/QuickBrochure/${viewProperty.id}`,
                          "_blank",
                        )
                      }
                      className="text-sm text-blue-600 underline truncate block"
                    >
                      View Brochure
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmartScheduler;

//=================+Working=================

// import React, { useState, useEffect, useMemo, useRef } from "react";
// import Menubar from "../Menubar/Menubar";
// import { Outlet } from "react-router-dom";
// import "../Dashboard/Dashboard.css";
// import axios from "axios";
// import { FaEye } from "react-icons/fa";
// import { MdCancel } from "react-icons/md";
// import {
//   Share2,
//   MessageCircle,
//   LayoutDashboard,
//   Globe,
//   Link as LinkIcon,
// } from "lucide-react";
// import { API_URL } from "../../Config";
// import { Search } from "lucide-react";
// import SearchProperty from "./SearchProperty";
// import { createPortal } from "react-dom";

// // const buildWebsiteUrl = (property) => {
// //   const isHotel = property.propertytype === "Hotel";
// //   return isHotel
// //     ? `https://zorbastays.com/Individualhotel/${property.id}/Hotel/${encodeURIComponent(property.propertyname)}/${encodeURIComponent(property.destination)}/${encodeURIComponent(property.location)}`
// //     : `https://zorbastays.com/IndividualProperty/${property.id}/${encodeURIComponent(property.propertytype)}/${encodeURIComponent(property.propertyname)}/${encodeURIComponent(property.destination)}/${encodeURIComponent(property.location)}/${property.maxadult}/${property.maxbed}/${property.maxbath || 0}`;
// // };
// const buildWebsiteUrl = (property) => {
//   const isHotel = property.propertytype === "Hotel";
//   const type = isHotel ? "hotel" : "villa";
//   const guest = isHotel ? 0 : property.maxadult;
//   const bedroom = isHotel ? 0 : property.maxbed;
//   const bathroom = isHotel ? 0 : property.maxbath || 0;

//   return `https://zorbastays.com/property-details/${property.id}/${type}/${encodeURIComponent(property.propertyname)}/${encodeURIComponent(property.destination)}/${encodeURIComponent(property.location)}/guest=${guest}/bedroom=${bedroom}/bathroom=${bathroom}`;
// };
// // NOTE: confirm the exact query param your dashboard edit page expects —
// // this assumes ?id=<propertyId> based on EditPropertyDetailElementor.
// const buildDashboardUrl = (property) =>
//   `/EditPropertyDetailElementor?id=${property.id}&mode=view`;
// const buildWhatsAppShareUrl = (property) => {
//   const websiteUrl = buildWebsiteUrl(property);
//   const message = `Check out ${property.propertyname} in ${property.location}, ${property.destination}: ${websiteUrl}`;
//   return `https://wa.me/?text=${encodeURIComponent(message)}`;
// };
// const ShareMenu = ({ property, align = "right", buttonClassName = "" }) => {
//   const [open, setOpen] = useState(false);
//   const [copied, setCopied] = useState(false);
//   const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
//   const menuRef = useRef(null);
//   const buttonRef = useRef(null);

//   useEffect(() => {
//     const handleClickOutside = (e) => {
//       if (
//         menuRef.current &&
//         !menuRef.current.contains(e.target) &&
//         buttonRef.current &&
//         !buttonRef.current.contains(e.target)
//       ) {
//         setOpen(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);
//   if (!property) return null;

//   const handleToggle = (e) => {
//     e.stopPropagation();
//     if (!open && buttonRef.current) {
//       const rect = buttonRef.current.getBoundingClientRect();
//       setMenuPos({
//         top: rect.bottom + window.scrollY + 4,
//         left: rect.right + window.scrollX - 224, // 224px = w-56 menu width, right-aligned
//       });
//     }
//     setOpen((prev) => !prev);
//   };

//   const handleCopyLink = async () => {
//     try {
//       await navigator.clipboard.writeText(buildWebsiteUrl(property));
//       setCopied(true);
//       setTimeout(() => setCopied(false), 1500);
//     } catch (err) {
//       console.error("Failed to copy link:", err);
//     }
//   };

//   return (
//     <div className="inline-block">
//       <button
//         ref={buttonRef}
//         type="button"
//         onClick={handleToggle}
//         className="text-gray-400 hover:text-[#1B2826] transition-colors ${buttonClassName}"
//         title="Share"
//       >
//         <Share2 size={16} />
//       </button>

//       {open &&
//         createPortal(
//           <div
//             ref={menuRef}
//             style={{
//               position: "absolute",
//               top: menuPos.top,
//               left: menuPos.left,
//             }}
//             className="w-52 ml-52 bg-white border rounded-lg shadow-lg z-[9999] py-1 text-sm"
//           >
//             <a
//               href={buildWhatsAppShareUrl(property)}
//               target="_blank"
//               rel="noopener noreferrer"
//               onClick={() => setOpen(false)}
//               className="flex items-center gap-2 px-4 py-2 hover:bg-green-50 text-gray-700"
//             >
//               <MessageCircle size={15} />
//               Share on WhatsApp
//             </a>

//             <a
//               href={buildDashboardUrl(property)}
//               target="_blank"
//               rel="noopener noreferrer"
//               onClick={() => setOpen(false)}
//               className="flex items-center gap-2 px-4 py-2 hover:bg-green-50 text-gray-700"
//             >
//               <LayoutDashboard size={15} />
//               Open in dashboard
//             </a>

//             <a
//               href={buildWebsiteUrl(property)}
//               target="_blank"
//               rel="noopener noreferrer"
//               onClick={() => setOpen(false)}
//               className="flex items-center gap-2 px-4 py-2 hover:bg-green-50 text-gray-700"
//             >
//               <Globe size={15} />
//               Open on website
//             </a>

//             <button
//               type="button"
//               onClick={() => {
//                 handleCopyLink();
//                 setOpen(false);
//               }}
//               className="w-full flex items-center gap-2 px-4 py-2 hover:bg-green-50 text-gray-700 text-left"
//             >
//               <LinkIcon size={15} />
//               {copied ? "Copied!" : "Copy link"}
//             </button>
//           </div>,
//           document.body,
//         )}
//     </div>
//   );
// };
// const SmartScheduler = () => {
//   const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
//   const [viewProperty, setViewProperty] = useState(null);
//   const [loadingViewProperty, setLoadingViewProperty] = useState(false);
//   const [isDrawerOpen, setIsDrawerOpen] = useState(false);
//   const [amenities, setAmenities] = useState([]);
//   const [selectedAmenities, setSelectedAmenities] = useState([]);
//   const [bedFilter, setBedFilter] = useState("");
//   const [amenitiesOpen, setAmenitiesOpen] = useState(false);
//   const [showMoreFilters, setShowMoreFilters] = useState(false);
//   const amenitiesRef = useRef(null);
//   const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
//     const savedState = localStorage.getItem("menubarOpen");
//     if (savedState !== null) return JSON.parse(savedState);
//     return window.innerWidth >= 1024;
//   });
//   const [ownerDropdownOpen, setOwnerDropdownOpen] = useState(false);
//   const ownerRef = useRef(null);
//   useEffect(() => {
//     const handleResize = () => {
//       const mobile = window.innerWidth < 1024;
//       setIsMobile(mobile);
//       if (mobile) setIsSidebarOpen(false);
//     };
//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);
//   useEffect(() => {
//     const fetchAmenities = async () => {
//       try {
//         const res = await axios.get(`${API_URL}/getAmenities`);
//         if (res.data.success) setAmenities(res.data.data);
//       } catch (err) {
//         console.error("Failed to fetch amenities:", err);
//       }
//     };
//     fetchAmenities();
//   }, []);
//   useEffect(() => {
//     const handleClickOutside = (e) => {
//       if (amenitiesRef.current && !amenitiesRef.current.contains(e.target)) {
//         setAmenitiesOpen(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);
//   useEffect(() => {
//     const handleClickOutside = (e) => {
//       if (ownerRef.current && !ownerRef.current.contains(e.target)) {
//         setOwnerDropdownOpen(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);

//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);
//   const [searchProperty, setSearchProperty] = useState("");
//   const [propertytype, setpropertytype] = useState("");
//   const [guests, setGuests] = useState("");
//   const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
//   const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
//   const [visibleCount, setVisibleCount] = useState(20);
//   const [destinationFilter, setdestinationFilter] = useState("");
//   const [locationFilter, setLocationFilter] = useState("");
//   const [viewPropertyData, setViewPropertyData] = useState(null);
//   // ── Real data state ──
//   const [properties, setProperties] = useState([]);
//   const [ownerFilter, setOwnerFilter] = useState("");
//   const owners = [
//     ...new Set(properties.map((p) => p.propertyowner).filter(Boolean)),
//   ].sort();

//   const [blockedMap, setBlockedMap] = useState({}); // { propertyname: Set of "YYYY-MM-DD" }
//   const [loadingProperties, setLoadingProperties] = useState(true);
//   const [loadingBlocked, setLoadingBlocked] = useState(false);
//   // near other state
//   const [searchInput, setSearchInput] = useState("");

//   useEffect(() => {
//     const t = setTimeout(() => setSearchProperty(searchInput), 250);
//     return () => clearTimeout(t);
//   }, [searchInput]);
//   // ── Fetch all properties ──
//   useEffect(() => {
//     const fetchProperties = async () => {
//       try {
//         setLoadingProperties(true);
//         const res = await axios.get(`${API_URL}/properties`);
//         setProperties(res.data);
//       } catch (err) {
//         console.error("Failed to fetch properties:", err);
//       } finally {
//         setLoadingProperties(false);
//       }
//     };
//     fetchProperties();
//   }, []);

//   // ── Bulk fetch blocked dates (once on mount) ──
//   useEffect(() => {
//     let cancelled = false;

//     const fetchAllBlocked = async () => {
//       setLoadingBlocked(true);
//       try {
//         const res = await axios.get(`${API_URL}/blocked-dates-all`);
//         if (cancelled) return;

//         if (res.data.success) {
//           const map = {};
//           for (const [name, { airbnb, db }] of Object.entries(
//             res.data.blocked,
//           )) {
//             map[name] = {
//               airbnb: new Set(airbnb),
//               db: new Set(db),
//             };
//           }
//           setBlockedMap(map);
//         }
//       } catch (err) {
//         console.error("Failed to fetch blocked dates:", err);
//       } finally {
//         if (!cancelled) setLoadingBlocked(false);
//       }
//     };

//     fetchAllBlocked();
//     return () => {
//       cancelled = true;
//     };
//   }, []);
//   // { [propertyId]: { "YYYY-MM-DD": price } }  — comes ONLY from property_price_global
//   const [priceMap, setPriceMap] = useState({});
//   const [loadingPrices, setLoadingPrices] = useState(false);

//   useEffect(() => {
//     const fetchAllPrices = async () => {
//       setLoadingPrices(true);
//       try {
//         const res = await axios.get(
//           `${API_URL}/property-prices-by-month?year=${selectedYear}&month=${selectedMonth + 1}`,
//         );
//         setPriceMap(res.data.success ? res.data.prices || {} : {});
//       } catch (err) {
//         console.error("Failed to fetch property prices:", err);
//         setPriceMap({});
//       } finally {
//         setLoadingPrices(false);
//       }
//     };
//     fetchAllPrices();
//   }, [selectedMonth, selectedYear]);
//   // ── Helpers ──
//   const years = Array.from(
//     { length: 11 },
//     (_, i) => new Date().getFullYear() - 5 + i,
//   );

//   const monthNames = [
//     "January",
//     "February",
//     "March",
//     "April",
//     "May",
//     "June",
//     "July",
//     "August",
//     "September",
//     "October",
//     "November",
//     "December",
//   ];

//   const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
//   const today = new Date();
//   const todayYear = today.getFullYear();
//   const todayMonth = today.getMonth();
//   const todayDate = today.getDate();

//   // Only show days from today onwards; hide all days for past months
//   const visibleDays = Array.from(
//     { length: daysInMonth },
//     (_, i) => i + 1,
//   ).filter((day) => {
//     if (selectedYear > todayYear) return true;
//     if (selectedYear === todayYear && selectedMonth > todayMonth) return true;
//     if (selectedYear === todayYear && selectedMonth === todayMonth)
//       return day >= todayDate;
//     return false; // past month — hide all days
//   });
//   const isBlocked = (propertyname, day) => {
//     const dateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
//     return blockedMap[propertyname]?.has(dateStr) ?? false;
//   };

//   // ── Unique destinations and locations for filters ──
//   const destinations = [
//     ...new Set(properties.map((p) => p.destination).filter(Boolean)),
//   ].sort();
//   const locations = [
//     ...new Set(
//       properties
//         .filter(
//           (p) => !destinationFilter || p.destination === destinationFilter,
//         )
//         .map((p) => p.location)
//         .filter(Boolean),
//     ),
//   ].sort();
//   useEffect(() => {
//     if (selectedYear === todayYear && selectedMonth < todayMonth) {
//       setSelectedMonth(todayMonth);
//     }
//   }, [selectedYear]);
//   useEffect(() => {
//     if (properties.length) {
//       // console.log("Sample property:", properties[0]);
//       // console.log(
//       //   "Sample addons:",
//       //   properties[0].addons,
//       //   typeof properties[0].addons,
//       // );
//     }
//   }, [properties]);
//   const getPropertyAmenityIds = (property) => {
//     const raw = property.addons ?? property.addons ?? property.amenities;
//     if (!raw) return [];

//     // Already an array
//     if (Array.isArray(raw)) {
//       return raw
//         .map((v) => Number(typeof v === "object" ? v.id : v))
//         .filter((n) => !isNaN(n));
//     }

//     // Comma-separated string
//     return String(raw)
//       .split(",")
//       .map((s) => s.trim())
//       .filter(Boolean)
//       .map(Number)
//       .filter((n) => !isNaN(n));
//   };
//   const toggleAmenity = (id) => {
//     setSelectedAmenities((prev) =>
//       prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id],
//     );
//   };
//   // ── Filter properties ──
//   const filteredProperties = useMemo(() => {
//     return properties.filter((p) => {
//       const matchesSearch = p.propertyname
//         .toLowerCase()
//         .includes(searchProperty.toLowerCase());
//       const matchesType = !propertytype || p.propertytype === propertytype;
//       const matchesGuests =
//         !guests || p.maxadult + (p.maxchild || 0) >= Number(guests);
//       const matchesDest =
//         !destinationFilter || p.destination === destinationFilter;
//       const matchesLoc = !locationFilter || p.location === locationFilter;
//       const matchesBed = !bedFilter || Number(p.maxbed) === Number(bedFilter);
//       const matchesAmenities =
//         selectedAmenities.length === 0 ||
//         selectedAmenities.every((id) => getPropertyAmenityIds(p).includes(id));
//       const matchesOwner =
//         !ownerFilter ||
//         p.propertyowner?.toLowerCase().includes(ownerFilter.toLowerCase());
//       return (
//         matchesSearch &&
//         matchesType &&
//         matchesGuests &&
//         matchesDest &&
//         matchesLoc &&
//         matchesBed &&
//         matchesAmenities &&
//         matchesOwner
//       );
//     });
//   }, [
//     properties,
//     searchProperty,
//     propertytype,
//     guests,
//     destinationFilter,
//     locationFilter,
//     bedFilter,
//     selectedAmenities,
//     ownerFilter,
//   ]);

//   const visibleProperties = filteredProperties.slice(0, visibleCount);

//   useEffect(() => {
//     setVisibleCount(20);
//   }, [
//     searchProperty,
//     propertytype,
//     guests,
//     destinationFilter,
//     locationFilter,
//     bedFilter,
//     selectedAmenities,
//     ownerFilter,
//   ]);
//   const filteredOwners = owners.filter((owner) =>
//     owner.toLowerCase().includes(ownerFilter.toLowerCase()),
//   );

//   const [active, setActive] = useState("Calendar");

//   const options = ["Calendar", "Search Availability"];

//   const handleTabChange = (tab) => {
//     setActive(tab);
//   };

//   return (
//     <div className="flex bg-[#F8F9FA]">
//       <Menubar
//         isOpen={isSidebarOpen}
//         setIsOpen={setIsSidebarOpen}
//         isMobile={isMobile}
//       />

//       <main
//         className={`flex-1 min-w-0 p-5 lg:p-6 transition-all duration-500 w-full ${
//           isMobile ? "ml-0" : isSidebarOpen ? "ml-64" : "ml-16"
//         }`}
//       >
//         <Outlet />

//         <p className="font-semibold text-xl text-gray-700 ml-10 lg:ml-0">
//           Smart Scheduler
//         </p>

//         {/* Segmented button */}
//         <div className="mt-5">
//           <div className="inline-flex border border-gray-300 rounded-lg overflow-hidden text-sm font-medium shadow-sm">
//             {options.map((option) => (
//               <button
//                 key={option}
//                 onClick={() => handleTabChange(option)}
//                 className={`px-4 py-2 transition-colors duration-200 focus:outline-none ${
//                   active === option
//                     ? "bg-[#1D2826] text-white"
//                     : "bg-white text-gray-500 hover:bg-green-50"
//                 }`}
//               >
//                 {option}
//               </button>
//             ))}
//           </div>
//         </div>

//         {active === "Calendar" && (
//           <div className="mt-8">
//             {/* Filters */}
//             <div className="mb-6">
//               <div className="flex flex-wrap items-center gap-3">
//                 {/* Property Type */}
//                 <select
//                   value={propertytype}
//                   onChange={(e) => setpropertytype(e.target.value)}
//                   className="border border-gray-300 rounded-lg px-4 py-3 text-sm w-48 focus-within:border-black focus-within:ring-1 focus-within:ring-black"
//                 >
//                   <option value="">All Property Types</option>
//                   <option value="Villa">Villa</option>
//                   <option value="Apartment">Apartment</option>
//                   <option value="Hotel">Hotel</option>
//                 </select>

//                 {/* Month */}
//                 <select
//                   value={selectedMonth}
//                   onChange={(e) => setSelectedMonth(Number(e.target.value))}
//                   className="border border-gray-300 rounded-lg px-4 py-3 text-sm w-40 focus-within:border-black focus-within:ring-1 focus-within:ring-black"
//                 >
//                   {monthNames.map((month, index) => {
//                     const isPast =
//                       selectedYear === todayYear && index < todayMonth;

//                     return (
//                       <option key={month} value={index} disabled={isPast}>
//                         {month}
//                       </option>
//                     );
//                   })}
//                 </select>

//                 {/* Year */}
//                 <select
//                   value={selectedYear}
//                   onChange={(e) => setSelectedYear(Number(e.target.value))}
//                   className="border border-gray-300 rounded-lg px-4 py-3 text-sm w-32 focus-within:border-black focus-within:ring-1 focus-within:ring-black"
//                 >
//                   {years.map((year) => (
//                     <option key={year} value={year} disabled={year < todayYear}>
//                       {year}
//                     </option>
//                   ))}
//                 </select>

//                 {/* destination */}
//                 <select
//                   value={destinationFilter}
//                   onChange={(e) => {
//                     setdestinationFilter(e.target.value);
//                     setLocationFilter("");
//                   }}
//                   className="border border-gray-300 rounded-lg px-4 py-3 text-sm w-48 focus-within:border-black focus-within:ring-1 focus-within:ring-black"
//                 >
//                   <option value="">All destinations</option>

//                   {destinations.map((d) => (
//                     <option key={d} value={d}>
//                       {d}
//                     </option>
//                   ))}
//                 </select>

//                 {/* Location */}
//                 <select
//                   value={locationFilter}
//                   onChange={(e) => setLocationFilter(e.target.value)}
//                   className="border border-gray-300 rounded-lg px-4 py-3 text-sm w-48 focus-within:border-black focus-within:ring-1 focus-within:ring-black"
//                 >
//                   <option value="">All Locations</option>

//                   {locations.map((l) => (
//                     <option key={l} value={l}>
//                       {l}
//                     </option>
//                   ))}
//                 </select>

//                 {/* More Filters */}
//                 <div className="relative">
//                   <button
//                     onClick={() => setShowMoreFilters((prev) => !prev)}
//                     className="border border-gray-300 rounded-lg px-4 py-3 text-sm bg-white focus-within:border-black focus-within:ring-1 focus-within:ring-black"
//                   >
//                     More Filters ▾
//                   </button>

//                   {showMoreFilters && (
//                     <div className="absolute right-0 mt-2 w-96 bg-white border rounded-xl shadow-xl p-5 z-50">
//                       <h3 className="font-semibold text-gray-700 mb-4">
//                         Advanced Filters
//                       </h3>

//                       <div className="grid grid-cols-2 gap-4">
//                         {/* Guests */}
//                         <input
//                           type="text"
//                           placeholder="Guests"
//                           value={guests}
//                           onChange={(e) =>
//                             setGuests(e.target.value.replace(/\D/g, ""))
//                           }
//                           className="border rounded-lg px-3 py-2 text-sm focus-within:border-black focus-within:ring-1 focus-within:ring-black"
//                         />

//                         {/* BHK */}
//                         <input
//                           type="text"
//                           placeholder="BHK"
//                           value={bedFilter}
//                           onChange={(e) =>
//                             setBedFilter(e.target.value.replace(/\D/g, ""))
//                           }
//                           className="border rounded-lg px-3 py-2 text-sm focus-within:border-black focus-within:ring-1 focus-within:ring-black"
//                         />

//                         {/* Owner Search */}
//                         <div
//                           className="relative flex-1 min-w-[250px]"
//                           ref={ownerRef}
//                         >
//                           <input
//                             type="text"
//                             placeholder="Managed By..."
//                             value={ownerFilter}
//                             onFocus={() => setOwnerDropdownOpen(true)}
//                             onChange={(e) => {
//                               setOwnerFilter(e.target.value);
//                               setOwnerDropdownOpen(true);
//                             }}
//                             className="w-full border rounded-lg px-3 py-2 text-sm col-span-2 focus-within:border-black focus-within:ring-1 focus-within:ring-black"
//                           />

//                           {ownerDropdownOpen && (
//                             <div className="absolute left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
//                               {filteredOwners.length === 0 ? (
//                                 <div className="px-4 py-3 text-sm text-gray-400">
//                                   No owners found
//                                 </div>
//                               ) : (
//                                 filteredOwners.map((owner) => (
//                                   <button
//                                     key={owner}
//                                     type="button"
//                                     onClick={() => {
//                                       setOwnerFilter(owner);
//                                       setOwnerDropdownOpen(false);
//                                     }}
//                                     className="w-full text-left px-4 py-3 hover:bg-green-50 transition"
//                                   >
//                                     <>
//                                       <span className="font-semibold">
//                                         {owner.substring(0, ownerFilter.length)}
//                                       </span>
//                                       {owner.substring(ownerFilter.length)}
//                                     </>
//                                   </button>
//                                 ))
//                               )}
//                             </div>
//                           )}
//                         </div>

//                         {/* Amenities */}
//                         <div className="relative col-span-2" ref={amenitiesRef}>
//                           <button
//                             type="button"
//                             onClick={() => setAmenitiesOpen((prev) => !prev)}
//                             className="w-full border rounded-lg px-3 py-2 text-sm flex justify-between items-center focus-within:border-black focus-within:ring-1 focus-within:ring-black"
//                           >
//                             <span>
//                               {selectedAmenities.length === 0
//                                 ? "All Amenities"
//                                 : `${selectedAmenities.length} Selected`}
//                             </span>

//                             <span>▾</span>
//                           </button>

//                           {amenitiesOpen && (
//                             <div className="absolute left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-white border rounded-lg shadow-lg p-2">
//                               {selectedAmenities.length > 0 && (
//                                 <button
//                                   type="button"
//                                   onClick={() => setSelectedAmenities([])}
//                                   className="text-xs text-red-500 underline mb-2"
//                                 >
//                                   Clear all
//                                 </button>
//                               )}

//                               {amenities
//                                 .filter(
//                                   (a) =>
//                                     !["kitchen", "bedroom"].includes(
//                                       a.name.toLowerCase(),
//                                     ),
//                                 )
//                                 .map((a) => (
//                                   <label
//                                     key={a.id}
//                                     className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 rounded cursor-pointer"
//                                   >
//                                     <input
//                                       type="checkbox"
//                                       checked={selectedAmenities.includes(a.id)}
//                                       onChange={() => toggleAmenity(a.id)}
//                                     />

//                                     <img
//                                       src={a.url}
//                                       alt={a.name}
//                                       className="w-8 h-8 object-contain"
//                                     />

//                                     <span className="text-sm">{a.name}</span>
//                                   </label>
//                                 ))}
//                             </div>
//                           )}
//                         </div>
//                       </div>
//                     </div>
//                   )}
//                 </div>

//                 {/* Clear */}
//                 <button
//                   onClick={() => {
//                     setpropertytype("");
//                     setGuests("");
//                     setBedFilter("");
//                     setdestinationFilter("");
//                     setLocationFilter("");
//                     setSelectedAmenities([]);
//                     setOwnerFilter("");
//                   }}
//                   className="border border-red-300 text-red-600 rounded-lg px-4 py-3 text-sm hover:bg-red-50"
//                 >
//                   Clear
//                 </button>
//               </div>
//             </div>
//             {/* Legend */}
//             <div className="flex gap-4 mb-4 text-xs text-gray-500">
//               <span className="flex items-center gap-1">
//                 <span className="w-3 h-3 rounded bg-red-100 inline-block"></span>{" "}
//                 Airbnb Blocked
//               </span>
//               <span className="flex items-center gap-1">
//                 <span className="w-3 h-3 rounded bg-green-100 inline-block"></span>{" "}
//                 Zorba Booked
//               </span>
//               <span className="flex items-center gap-1">
//                 <span className="w-3 h-3 rounded bg-white border inline-block"></span>{" "}
//                 Available
//               </span>
//             </div>

//             {loadingProperties ? (
//               <div className="text-center py-10 text-gray-400">
//                 Loading Dates...
//               </div>
//             ) : (
//               <>
//                 <div className="overflow-auto border rounded-xl">
//                   <table className="min-w-max w-full border-collapse text-sm">
//                     <thead>
//                       <tr className="bg-gray-100 border-b text-gray-500">
//                         <th className="sticky left-0 z-20 min-w-[200px] p-3 bg-gray-100">
//                           <input
//                             type="text"
//                             placeholder="Search Property..."
//                             value={searchInput}
//                             onChange={(e) => setSearchInput(e.target.value)}
//                             className="w-full text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-0 focus:border-black px-3 py-2"
//                           />
//                         </th>
//                         {visibleDays.map((day) => {
//                           // const today = new Date();
//                           const isToday =
//                             day === today.getDate() &&
//                             selectedMonth === today.getMonth() &&
//                             selectedYear === today.getFullYear();
//                           return (
//                             <th
//                               key={day}
//                               className={`min-w-[110px] p-3 text-center whitespace-nowrap ${
//                                 isToday
//                                   ? "bg-green-100 text-green-700 font-bold"
//                                   : ""
//                               }`}
//                             >
//                               {day} {monthNames[selectedMonth].slice(0, 3)}
//                             </th>
//                           );
//                         })}
//                       </tr>
//                     </thead>

//                     <tbody>
//                       {visibleProperties.map((property) => (
//                         <tr key={property.id} className="border-b">
//                           <td className="sticky left-0 bg-white z-10 p-3 font-medium border-r">
//                             <div>
//                               <img
//                                 src={
//                                   property.imagenew
//                                     ? property.imagenew
//                                     : property.hotelbanner
//                                 }
//                                 alt={property.propertyname}
//                                 className="mx-auto w-auto h-28 aspect-[5/3] object-cover rounded-md bg-gray-100"
//                               />
//                             </div>

//                             <div className="flex justify-between items-start gap-3 mt-3">
//                               <div>
//                                 <div className="font-semibold">
//                                   {property.propertyname}
//                                 </div>

//                                 <div className="text-xs text-gray-500 mt-1">
//                                   {property.propertytype} • {property.maxadult}{" "}
//                                   Guests • {property.maxbed} Beds
//                                 </div>

//                                 <div className="text-xs text-gray-400">
//                                   {property.location}, {property.destination}
//                                 </div>

//                                 {/* {property.currentprice &&
//                                 Number(property.currentprice) !== 0 ? (
//                                   <div className="text-xs text-green-700 font-semibold mt-1">
//                                     ₹
//                                     {Number(
//                                       property.currentprice,
//                                     ).toLocaleString()}
//                                     /night
//                                   </div>
//                                 ) : (
//                                   <div className="text-xs text-gray-400 font-semibold mt-1">
//                                     Price N/A
//                                   </div>
//                                 )} */}
//                                 {(() => {
//                                   const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
//                                   const price =
//                                     priceMap[property.id]?.[todayStr];

//                                   return price ? (
//                                     <div className="text-xs text-green-700 font-semibold mt-1 flex items-center gap-1">
//                                       <Globe
//                                         size={11}
//                                         className="text-blue-500"
//                                         title="Price from property_price_global"
//                                       />
//                                       ₹{price.toLocaleString()}/night
//                                     </div>
//                                   ) : (
//                                     <div className="text-xs text-gray-400 font-semibold mt-1">
//                                       Price N/A
//                                     </div>
//                                   );
//                                 })()}
//                                 {/* <button
//                                   type="button"
//                                   onClick={() => {
//                                     const isHotel =
//                                       property.propertytype === "Hotel";
//                                     const url = isHotel
//                                       ? `https://zorbastays.com/Individualhotel/${property.id}/Hotel/${encodeURIComponent(property.propertyname)}/${encodeURIComponent(property.destination)}/${encodeURIComponent(property.location)}`
//                                       : `https://zorbastays.com/IndividualProperty/${property.id}/${encodeURIComponent(property.propertytype)}/${encodeURIComponent(property.propertyname)}/${encodeURIComponent(property.destination)}/${encodeURIComponent(property.location)}/${property.maxadult}/${property.maxbed}/${property.maxbath || 0}`;

//                                     window.open(url, "_blank");
//                                   }}
//                                   className="absolute bottom-2 mr-2 right-0 text-xs text-[#4c6863] underline hover:text-[#1B2826]"
//                                 >
//                                   Open Property
//                                 </button> */}
//                                 <div className="absolute bottom-2 right-2 flex items-center gap-2">
//                                   <button
//                                     type="button"
//                                     onClick={() =>
//                                       window.open(
//                                         buildWebsiteUrl(property),
//                                         "_blank",
//                                       )
//                                     }
//                                     className="text-xs text-[#4c6863] underline hover:text-[#1B2826]"
//                                   >
//                                     Open Property
//                                   </button>
//                                 </div>
//                               </div>

//                               <div className="space-y-3">
//                                 <FaEye
//                                   size={22}
//                                   onClick={async () => {
//                                     setViewProperty(property);

//                                     setTimeout(() => {
//                                       setIsDrawerOpen(true);
//                                     }, 10);
//                                     setViewPropertyData(null);
//                                     setLoadingViewProperty(true);
//                                     try {
//                                       const isHotel =
//                                         property.propertytype === "Hotel";

//                                       const res = await axios.get(
//                                         isHotel
//                                           ? `${API_URL}/hotel/${property.id}`
//                                           : `${API_URL}/properties/${property.id}`,
//                                       );
//                                       // Normalize so the rest of the drawer code works the same
//                                       const data = isHotel
//                                         ? {
//                                             ...property,
//                                             ...res.data.property,
//                                             ...res.data.hotelInfo,
//                                             imagenew: property.imagenew,
//                                             hotelbanner: property.hotelbanner,
//                                           }
//                                         : {
//                                             ...property,
//                                             ...res.data,
//                                             imagenew: property.imagenew,
//                                             hotelbanner: property.hotelbanner,
//                                           };

//                                       setViewPropertyData(data);
//                                     } catch (err) {
//                                       console.error(
//                                         "Failed to fetch property details:",
//                                         err,
//                                       );
//                                     } finally {
//                                       setLoadingViewProperty(false);
//                                     }
//                                   }}
//                                   className="border rounded-full p-0.5 flex-shrink-0 cursor-pointer hover:border-green-700 hover:scale-110 transition-all duration-300"
//                                 />

//                                 <ShareMenu property={property} />
//                               </div>
//                             </div>
//                           </td>

//                           {visibleDays.map((day) => {
//                             const dateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
//                             const blocked = blockedMap[property.propertyname];
//                             const isAirbnbBlocked =
//                               blocked?.airbnb?.has(dateStr) ?? false;
//                             const isDbBlocked =
//                               blocked?.db?.has(dateStr) ?? false;
//                             const isAnyBlocked = isAirbnbBlocked || isDbBlocked;

//                             const today = new Date();
//                             const isToday =
//                               day === today.getDate() &&
//                               selectedMonth === today.getMonth() &&
//                               selectedYear === today.getFullYear();

//                             const price = priceMap[property.id]?.[dateStr]; // number | undefined

//                             return (
//                               <td
//                                 key={day}
//                                 className={`border text-center p-2 ${isToday ? "bg-green-50" : ""}`}
//                               >
//                                 {loadingBlocked ? (
//                                   <div className="rounded-lg min-h-[72px] bg-gray-100 animate-pulse" />
//                                 ) : (
//                                   <div
//                                     className={`rounded-lg p-2 min-h-[72px] flex flex-col items-center justify-center transition-all ${
//                                       isAirbnbBlocked
//                                         ? "bg-red-50 text-red-400"
//                                         : isDbBlocked
//                                           ? "bg-green-50 text-green-600"
//                                           : "bg-white text-gray-800"
//                                     }`}
//                                   >
//                                     {/* Globe only when we have a price from property_price_global */}
//                                     {!isAnyBlocked && price && (
//                                       <Globe
//                                         size={12}
//                                         className="text-blue-500 mb-0.5"
//                                         title="Price from property_price_global"
//                                       />
//                                     )}

//                                     <span className="text-sm font-medium">
//                                       {day}
//                                     </span>

//                                     {isAnyBlocked ? (
//                                       <span className="text-xs mt-1">
//                                         {isAirbnbBlocked ? "Airbnb" : "Booked"}
//                                       </span>
//                                     ) : price ? (
//                                       <span className="text-xs text-green-700 font-semibold mt-1">
//                                         ₹{price.toLocaleString()}
//                                       </span>
//                                     ) : (
//                                       <span className="text-xs text-gray-400 font-semibold mt-1">
//                                         N/A
//                                       </span>
//                                     )}
//                                   </div>
//                                 )}
//                               </td>
//                             );
//                           })}
//                         </tr>
//                       ))}
//                     </tbody>
//                   </table>
//                 </div>
//                 {/*
//               {loadingBlocked && (
//                 <p className="text-xs text-gray-400 mt-2 text-center">
//                   Loading availability data...
//                 </p>
//               )} */}

//                 {visibleCount < filteredProperties.length && (
//                   <div className="flex justify-center mt-5">
//                     <button
//                       onClick={() => setVisibleCount((prev) => prev + 10)}
//                       className="px-5 py-2 rounded-lg bg-green-900 text-white text-sm font-medium hover:scale-95 hover:opacity-90 transition"
//                     >
//                       Load More
//                     </button>
//                   </div>
//                 )}
//               </>
//             )}
//           </div>
//         )}

//         {active === "Search Availability" && (
//           <div>
//             <SearchProperty />
//           </div>
//         )}
//       </main>

//       <div className="relative z-50">
//         {viewProperty && (
//           <div
//             onClick={() => {
//               setIsDrawerOpen(false);

//               setTimeout(() => {
//                 setViewProperty(null);
//                 setViewPropertyData(null);
//               }, 500);
//             }}
//             className={`fixed inset-0 bg-black/30 z-40 transition-opacity duration-500 ${
//               isDrawerOpen ? "opacity-100" : "opacity-0"
//             }`}
//           />
//         )}

//         <div
//           className={`fixed top-0 right-0 h-full w-[85%] md:w-[640px] bg-white z-50 shadow-lg transform transition-transform duration-500 ease-in-out ${
//             isDrawerOpen ? "translate-x-0" : "translate-x-full"
//           } panel_popupForm`}
//         >
//           {/* // <div className="fixed top-0 right-0 h-full w-[85%] md:w-[640px] bg-white z-50 shadow-lg"> */}
//           <div className="p-4 flex justify-between items-center border-b header_popupForm">
//             <h2 className="text-[#1D2826] text-lg font-semibold flex items-center">
//               <span>Property Details</span>

//               {viewProperty && (
//                 <span className="ml-4">
//                   <ShareMenu property={viewProperty} />
//                 </span>
//               )}
//             </h2>

//             <div className="flex gap-3 items-center">
//               <button
//                 type="button"
//                 onClick={() =>
//                   window.open(buildWebsiteUrl(viewProperty), "_blank")
//                 }
//                 className="zorba_btn text-sm uppercase flex items-center justify-center gap-2"
//                 style={{ "--clr": "#2c3d3a" }}
//               >
//                 Open Url
//                 <span className="zorba_btn__icon-wrapper">
//                   <svg
//                     viewBox="0 0 14 15"
//                     fill="none"
//                     xmlns="http://www.w3.org/2000/svg"
//                     className="zorba_btn__icon-svg"
//                     width="10"
//                   >
//                     <path
//                       d="M13.376 11.552l-.264-10.44-10.44-.24.024 2.28 6.96-.048L.2 12.56l1.488 1.488 9.432-9.432-.048 6.912 2.304.024z"
//                       fill="currentColor"
//                     ></path>
//                   </svg>

//                   <svg
//                     viewBox="0 0 14 15"
//                     fill="none"
//                     width="10"
//                     xmlns="http://www.w3.org/2000/svg"
//                     className="zorba_btn__icon-svg zorba_btn__icon-svg--copy"
//                   >
//                     <path
//                       d="M13.376 11.552l-.264-10.44-10.44-.24.024 2.28 6.96-.048L.2 12.56l1.488 1.488 9.432-9.432-.048 6.912 2.304.024z"
//                       fill="currentColor"
//                     ></path>
//                   </svg>
//                 </span>
//               </button>

//               <button
//                 onClick={() => {
//                   setIsDrawerOpen(false);
//                   setTimeout(() => {
//                     setViewProperty(null);
//                     setViewPropertyData(null);
//                   }, 500);
//                 }}
//                 className="text-gray-500 hover:text-black text-xl"
//               >
//                 <MdCancel />
//               </button>
//             </div>
//           </div>
//           {viewProperty && (
//             <div className="p-6 max-h-[90vh] overflow-y-auto">
//               {/* Banner Image */}
//               {loadingViewProperty ? (
//                 <div className="w-full h-48 rounded-xl bg-gray-100 animate-pulse mb-6" />
//               ) : viewPropertyData?.imagenew ||
//                 viewPropertyData?.hotelbanner ? (
//                 <img
//                   src={
//                     viewPropertyData.imagenew || viewPropertyData.hotelbanner
//                   }
//                   alt={viewProperty.propertyname}
//                   className="w-full h-auto min-h-80 object-cover rounded-xl mb-6 bg-gray-100"
//                 />
//               ) : (
//                 <div className="w-full h-52 rounded-xl bg-gray-100 flex items-center justify-center text-gray-400 text-sm mb-6">
//                   No image available
//                 </div>
//               )}
//               <div className="grid grid-cols-2 gap-4">
//                 {/* Villa Name */}
//                 <div className="col-span-2 border border-gray-300 rounded-lg px-4 py-3 relative">
//                   <span className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500">
//                     Villa Name
//                   </span>
//                   <p className="text-sm font-semibold text-gray-800">
//                     {viewProperty.propertyname}
//                   </p>
//                 </div>

//                 {/* Property Type */}
//                 <div className="border border-gray-300 rounded-lg px-4 py-3 relative">
//                   <span className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500">
//                     Property Type
//                   </span>
//                   <p className="text-sm text-gray-800">
//                     {viewProperty.propertytype}
//                   </p>
//                 </div>

//                 {/* Guests */}
//                 <div className="border border-gray-300 rounded-lg px-4 py-3 relative">
//                   <span className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500">
//                     Guests
//                   </span>
//                   <p className="text-sm text-gray-800">
//                     {viewProperty.maxadult} Adults
//                   </p>
//                 </div>

//                 {/* Beds */}
//                 <div className="border border-gray-300 rounded-lg px-4 py-3 relative">
//                   <span className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500">
//                     Beds
//                   </span>
//                   <p className="text-sm text-gray-800">
//                     {viewProperty.maxbed} Beds
//                   </p>
//                 </div>

//                 {/* Baths */}
//                 <div className="border border-gray-300 rounded-lg px-4 py-3 relative">
//                   <span className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500">
//                     Bath
//                   </span>
//                   <p className="text-sm text-gray-800">
//                     {viewProperty.maxbath ?? "-"} Baths
//                   </p>
//                 </div>

//                 {/* destination */}
//                 <div className="border border-gray-300 rounded-lg px-4 py-3 relative">
//                   <span className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500">
//                     destination
//                   </span>
//                   <p className="text-sm text-gray-800">
//                     {viewProperty.destination}
//                   </p>
//                 </div>

//                 {/* Location */}
//                 <div className="border border-gray-300 rounded-lg px-4 py-3 relative">
//                   <span className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500">
//                     Location
//                   </span>
//                   <p className="text-sm text-gray-800">
//                     {viewProperty.location}
//                   </p>
//                 </div>

//                 {/* Price */}
//                 <div className="border border-gray-300 rounded-lg px-4 py-3 relative">
//                   <span className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500">
//                     Price Per Night
//                   </span>
//                   <p className="text-sm text-gray-800">
//                     ₹{Number(viewProperty.currentprice).toLocaleString()}
//                   </p>
//                 </div>

//                 {/* Map Link */}

//                 <div className="border border-gray-300 rounded-lg px-4 py-3 relative">
//                   <span className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500">
//                     Google Map
//                   </span>
//                   {loadingViewProperty ? (
//                     <div className="h-4 w-24 bg-gray-100 animate-pulse rounded" />
//                   ) : viewPropertyData?.maplink ? (
//                     <a
//                       href={viewPropertyData.maplink}
//                       target="_blank"
//                       rel="noopener noreferrer"
//                       className="text-sm text-blue-600 underline truncate block"
//                     >
//                       View on Map
//                     </a>
//                   ) : (
//                     <p className="text-sm text-gray-400">Not available</p>
//                   )}
//                 </div>
//                 {/* Brochure */}
//                 {/* <div className="border border-gray-300 rounded-lg px-4 py-3 relative">
//                   <span className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500">
//                     Quick Brochure
//                   </span>

//                   <button
//                     type="button"
//                     onClick={() =>
//                       window.open(`/QuickBrochure/${viewProperty.id}`, "_blank")
//                     }
//                     className="text-sm text-blue-600 underline truncate block"
//                   >
//                     View Brochure
//                   </button>
//                 </div> */}
//                 {viewProperty.propertytype !== "Hotel" && (
//                   <div className="border border-gray-300 rounded-lg px-4 py-3 relative">
//                     <span className="absolute -top-2.5 left-3 bg-white px-1 text-xs text-gray-500">
//                       Quick Brochure
//                     </span>
//                     <button
//                       type="button"
//                       onClick={() =>
//                         window.open(
//                           `/QuickBrochure/${viewProperty.id}`,
//                           "_blank",
//                         )
//                       }
//                       className="text-sm text-blue-600 underline truncate block"
//                     >
//                       View Brochure
//                     </button>
//                   </div>
//                 )}
//               </div>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default SmartScheduler;
